/**
 * Application shell / layout.
 *
 * Renders the persistent navigation, environment indicator (FR-008), auth controls, and the
 * routed page outlet. Wrapped by AuthGate at the root, so it only renders for signed-in users.
 */

import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useAuth, useUser, useProviderLogout } from '../auth/hooks';
import { getConfig, getActiveApiKey } from '../config';
import { EnvIndicator } from '../components/EnvIndicator';
import { APP_DISPLAY_NAME } from '../appMeta';

const NAV_ITEMS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/items', label: 'Items' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/api-explorer', label: 'API Explorer' },
  { to: '/protected', label: 'Protected' },
  { to: '/auth-test', label: 'Auth Test' },
];

/**
 * Suspense fallback shown while a lazily-loaded route chunk resolves.
 */
export function RouteFallback(): JSX.Element {
  const { theme } = useTheme();
  return (
    <div style={{ padding: spacing.xl, textAlign: 'center', color: theme.colors.text.secondary }}>
      Loading…
    </div>
  );
}

export default function AppLayout(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const auth = useAuth();
  const user = useUser();
  const { signoutRedirect } = useProviderLogout();
  const config = getConfig();

  // Force light theme for a consistent template default.
  React.useEffect(() => {
    setTheme('light');
  }, [setTheme]);

  const bg = theme.colors.background.default;
  const text = theme.colors.text.primary;
  const muted = theme.colors.text.secondary;
  const primary = theme.colors.primary[500];
  const isProd = config.environment === 'prod';

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text }}>
      {isProd && (
        <div
          style={{
            padding: `${spacing.xs} ${spacing.md}`,
            background: theme.colors.semantic.error.default,
            color: theme.colors.text.inverse,
            textAlign: 'center',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          ⚠️ PRODUCTION ENVIRONMENT — Actions affect real data
        </div>
      )}

      <header
        style={{
          borderBottom: `1px solid ${theme.colors.border.default}`,
          padding: `${spacing.sm} ${spacing.lg}`,
        }}
      >
        <Flex justify="between" align="center" wrap="wrap" gap="sm">
          <Flex align="center" gap="md" wrap="wrap">
            <strong style={{ fontSize: '1.1rem', letterSpacing: '0.02em' }}>{APP_DISPLAY_NAME}</strong>
            <nav>
              <Flex align="center" gap="sm">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    style={({ isActive }) => ({
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? primary : muted,
                      padding: `${spacing.xs} ${spacing.sm}`,
                      borderRadius: '0.375rem',
                    })}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </Flex>
            </nav>
          </Flex>

          <Flex align="center" gap="sm">
            <EnvIndicator
              environment={config.environment}
              authMode={config.authMode}
              activeApiKey={getActiveApiKey()}
            />
            {auth.isAuthenticated && (
              <>
                <span style={{ fontSize: '0.85rem', color: muted }}>{user?.email}</span>
                <Button variant="outline" color="error" size="sm" onClick={() => signoutRedirect()}>
                  Sign out
                </Button>
              </>
            )}
          </Flex>
        </Flex>
      </header>

      <main style={{ padding: spacing.lg, maxWidth: '960px', margin: '0 auto' }}>
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="md">
              <React.Suspense fallback={<RouteFallback />}>
                <Outlet />
              </React.Suspense>
            </Stack>
          </Card.Body>
        </Card>
      </main>
    </div>
  );
}
